import { ArrowRightIcon, CopyButton } from "./components.tsx";
import { usePageMeta } from "./pageMeta.ts";
import { settingsPath } from "./paths.ts";
import { agenticUiSocialMeta } from "./socialMeta.ts";

export const agenticUiLaunchUrl = settingsPath;

export const coderPromptTemplate =
  `Read https://prompt2bot.com/llms.txt for the API reference.

My prompt2bot API token: <YOUR_P2B_TOKEN>
My system API endpoints and docs: <YOUR_API_ENDPOINTS_OR_OPENAPI_URL>

Using the @prompt2bot/client SDK or the prompt2bot HTTP API:
1. Create a bot using createBotApi.
2. Register my API endpoints as remote tools using setCustomTools with typed parameter schemas.
3. Set the system prompt so the agent validates user input, calls the right endpoints, and asks for confirmation before executing sensitive mutations.`;

export const sampleToolPayload =
  `// Your API receives cryptographically signed tool requests:
POST https://your-api.example.com/tools/orders
Headers:
  x-signature: <HMAC-SHA256 signature>
  x-timestamp: 1715000000000

Body:
{
  "orderId": "ord_9821",
  "action": "refund",
  "amount": 15.00,
  "reason": "shipping_delay"
}`;

const sectionClass = "py-16 px-4 sm:px-6 max-w-5xl mx-auto w-full";
const cardClass =
  "p-6 sm:p-8 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised transition-all duration-200";

const CheckIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SparklesIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
  </svg>
);

const TerminalIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="4 17 10 11 4 5" />
    <line x1="12" y1="19" x2="20" y2="19" />
  </svg>
);

const ShieldIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const LayersIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
);

const MessageSquareIcon = (
  { className = "w-6 h-6" }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const skippedInfraItems = [
  {
    title: "Context window management",
    description:
      "No manual prompt packing or trimming. prompt2bot tracks session limits and handles dynamic summarization automatically.",
  },
  {
    title: "Conversation memory & history",
    description:
      "Immutable, append-only event streams in Deno KV. Conversations persist across turns, devices, and sessions.",
  },
  {
    title: "Built-in conversation viewers",
    description:
      "Full monitoring out of the box with View Chat. Inspect exact model thoughts, tool parameters, execution latency, and token costs.",
  },
  {
    title: "Cryptographic HMAC signing",
    description:
      "Tool requests to your backend are signed with SHA-256 using your bot Webhook Secret. Zero forged or replayed requests.",
  },
  {
    title: "Multi-channel adapters",
    description:
      "No custom connectors for WhatsApp Cloud API, Telegram Bot API, Slack events, or web sockets. It is all pre-wired.",
  },
  {
    title: "Human in the loop approvals",
    description:
      "Gate irreversible API calls with 1-tap confirmation. Admins approve or deny operations directly from WhatsApp or Slack.",
  },
];

export const AgenticUiPage = () => {
  usePageMeta({
    title: agenticUiSocialMeta.title,
    description: agenticUiSocialMeta.description,
    image: agenticUiSocialMeta.image,
    twitterCard: "summary_large_image",
  });

  return (
    <div className="flex flex-col w-full text-text-primary-light dark:text-text-primary">
      {/* Hero Section */}
      <section className="pt-16 pb-20 px-4 sm:px-6 max-w-5xl mx-auto w-full text-center">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-6 bg-accent-muted text-accent-light dark:text-accent border border-accent/20">
          <SparklesIcon className="w-3.5 h-3.5" />
          Agentic UI by prompt2bot
        </div>

        <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tight mb-6 max-w-4xl mx-auto leading-[1.12]">
          You have an API. We give you the agent layer.
        </h1>

        <p className="text-lg sm:text-xl text-text-secondary-light dark:text-text-secondary max-w-3xl mx-auto leading-relaxed mb-10">
          Building agent infrastructure from scratch is a pain. You end up
          wrestling with context windows, conversation memory, viewers,
          streaming, tool routing, and channel adapters. prompt2bot provides all
          the agent infrastructure out of the box. You just bring your API.
        </p>

        <div className="flex items-center justify-center mb-12">
          <a
            href={agenticUiLaunchUrl}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            <span>Get your API token in one click</span>
            <ArrowRightIcon className="w-5 h-5" />
          </a>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto text-left text-xs sm:text-sm text-text-secondary-light dark:text-text-secondary">
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Zero context or memory code</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Built-in conversation viewer</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Encrypted web chat widget</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Native WhatsApp and Telegram</span>
          </div>
        </div>
      </section>

      {/* The Problem: Building Agent Infrastructure is a Trap */}
      <section className="py-12 px-4 sm:px-6 max-w-4xl mx-auto w-full">
        <div className="p-8 sm:p-10 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised space-y-5 text-left">
          <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent">
            The Reality
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">
            Calling an LLM is 5% of the work. The rest is infrastructure.
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary leading-relaxed">
            Wiring a model call to an API endpoint sounds straightforward on day
            one. Then reality hits.
          </p>
          <p className="text-text-secondary-light dark:text-text-secondary leading-relaxed">
            You have to manage conversation context so turns do not blow past
            model token limits. You need persistent memory so the agent
            remembers earlier requests across days. You need conversation
            viewers and debug traces to figure out why an agent called the wrong
            parameter in production. And you have to write custom adapters for
            web widgets, WhatsApp, and Telegram.
          </p>
          <p className="text-text-secondary-light dark:text-text-secondary leading-relaxed">
            prompt2bot solves the entire infrastructure layer. Your system stays
            a regular API. prompt2bot provides the multi-turn agent runtime, the
            history store, the conversation viewers, and the channel gateways.
          </p>
        </div>
      </section>

      {/* How it Works: 3 Steps */}
      <section className={sectionClass}>
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            How to connect everything in three steps
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
            Give your coding agent our llms.txt and your API token. It handles
            the rest.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 text-left">
          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-10 h-10 rounded-xl bg-accent-muted text-accent flex items-center justify-center font-bold text-base mb-4">
                1
              </div>
              <h3 className="font-display text-lg font-bold mb-2">
                Get your prompt2bot token
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
                Generate an API token from your Settings page. This token lets
                your coding agent manage bots, register tools, and configure
                prompts programmatically.
              </p>
            </div>
            <div className="mt-4 pt-4 border-t border-border-subtle-light dark:border-border-subtle text-xs text-text-tertiary-light dark:text-text-tertiary font-mono">
              p2b_live_...
            </div>
          </div>

          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-10 h-10 rounded-xl bg-accent-muted text-accent flex items-center justify-center font-bold text-base mb-4">
                2
              </div>
              <h3 className="font-display text-lg font-bold mb-2">
                Hand prompt2bot.com/llms.txt to your coder agent
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
                Give Claude Code, Cursor, OpenCode, or Aider our llms.txt file
                along with your API docs. It reads the client spec and maps your
                endpoints to prompt2bot tools.
              </p>
            </div>
            <div className="mt-4 pt-4 border-t border-border-subtle-light dark:border-border-subtle text-xs text-text-tertiary-light dark:text-text-tertiary font-mono">
              https://prompt2bot.com/llms.txt
            </div>
          </div>

          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-10 h-10 rounded-xl bg-accent-muted text-accent flex items-center justify-center font-bold text-base mb-4">
                3
              </div>
              <h3 className="font-display text-lg font-bold mb-2">
                Deploy across Web, WhatsApp and Telegram
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
                Your agent is immediately live. Drop the end-to-end encrypted
                chat widget into your web app, or connect your WhatsApp and
                Telegram channels in one click.
              </p>
            </div>
            <div className="mt-4 pt-4 border-t border-border-subtle-light dark:border-border-subtle text-xs text-text-tertiary-light dark:text-text-tertiary font-mono">
              Live in minutes
            </div>
          </div>
        </div>
      </section>

      {/* Copyable Prompt for Coding Agents */}
      <section className="py-12 px-4 sm:px-6 max-w-5xl mx-auto w-full">
        <div className={cardClass}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TerminalIcon className="w-5 h-5 text-accent" />
              <span className="text-sm font-bold">
                Prompt to give your coding agent
              </span>
            </div>
            <CopyButton text={coderPromptTemplate} />
          </div>
          <p className="text-xs text-text-secondary-light dark:text-text-secondary mb-3">
            Paste this into Claude Code, Cursor, OpenCode, or Aider:
          </p>
          <pre className="p-4 rounded-xl bg-surface-overlay-light dark:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle font-mono text-xs overflow-x-auto leading-relaxed text-text-secondary-light dark:text-text-secondary">
            <code>{coderPromptTemplate}</code>
          </pre>
        </div>
      </section>

      {/* Channel Deployment Options */}
      <section className={sectionClass}>
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Deploy where your users already are
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
            One API connection powers every channel. No separate SDKs or
            protocols.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 text-left">
          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-5">
              <ShieldIcon className="w-6 h-6" />
            </div>
            <h3 className="font-display text-xl font-bold mb-3">
              End-to-end encrypted web widget
            </h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
              Embed a private, secure chat experience directly in your web
              application.
            </p>
            <ul className="space-y-2.5 text-sm text-text-secondary-light dark:text-text-secondary">
              <li className="flex items-start gap-2">
                <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <span>
                  <strong>Client-side encryption:</strong>{" "}
                  Conversations are encrypted end-to-end so sensitive customer
                  queries remain private.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <span>
                  <strong>Single script tag:</strong>{" "}
                  Add one snippet of code to your website or portal and you are
                  done.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <span>
                  <strong>Custom styling:</strong>{" "}
                  Match your brand colors, fonts, and avatar icons effortlessly.
                </span>
              </li>
            </ul>
          </div>

          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-5">
              <MessageSquareIcon className="w-6 h-6" />
            </div>
            <h3 className="font-display text-xl font-bold mb-3">
              Popular instant messaging networks
            </h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
              Let users interact with your system on the chat apps they use all
              day.
            </p>
            <ul className="space-y-2.5 text-sm text-text-secondary-light dark:text-text-secondary">
              <li className="flex items-start gap-2">
                <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <span>
                  <strong>WhatsApp:</strong>{" "}
                  Official WhatsApp Business Cloud API or private QR code links,
                  supporting 1:1 and team group chats.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <span>
                  <strong>Telegram:</strong>{" "}
                  Instant connection using your standard bot token.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <span>
                  <strong>Slack & Teams:</strong>{" "}
                  Put your backend tools right inside your internal team
                  channels.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* The Infrastructure You Skip Building */}
      <section className={sectionClass}>
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase mb-3 bg-accent-muted text-accent">
            <LayersIcon className="w-3.5 h-3.5" />
            Included Out of the Box
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            The infrastructure you skip building
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
            Everything between your API and the user is managed by prompt2bot.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 text-left">
          {skippedInfraItems.map((item) => (
            <div
              key={item.title}
              className="p-6 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised flex flex-col justify-between"
            >
              <div>
                <div className="w-8 h-8 rounded-lg bg-accent-muted text-accent flex items-center justify-center mb-3">
                  <CheckIcon className="w-4 h-4" />
                </div>
                <h3 className="font-display text-base font-bold mb-2">
                  {item.title}
                </h3>
                <p className="text-xs text-text-secondary-light dark:text-text-secondary leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Signed Requests Code Box */}
      <section className="py-12 px-4 sm:px-6 max-w-4xl mx-auto w-full text-left">
        <div className={cardClass}>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-bold">
              Secure HMAC request verification
            </span>
            <CopyButton text={sampleToolPayload} />
          </div>
          <p className="text-xs text-text-secondary-light dark:text-text-secondary mb-3">
            Every tool call sent to your API is cryptographically signed using
            your bot Webhook Secret.
          </p>
          <pre className="p-4 rounded-xl bg-surface-overlay-light dark:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle font-mono text-xs overflow-x-auto leading-relaxed text-text-secondary-light dark:text-text-secondary">
            <code>{sampleToolPayload}</code>
          </pre>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="py-20 px-4 sm:px-6 max-w-4xl mx-auto w-full text-center">
        <div className="p-8 sm:p-12 rounded-3xl border border-accent/20 bg-accent-muted/30 relative overflow-hidden space-y-6">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Ready to add an agent layer to your API?
          </h2>
          <p className="text-base sm:text-lg text-text-secondary-light dark:text-text-secondary max-w-2xl mx-auto leading-relaxed">
            Grab an API token, give prompt2bot.com/llms.txt to your coding
            agent, and deploy your agent across Web, WhatsApp, and Telegram in
            minutes.
          </p>
          <div className="pt-2 flex items-center justify-center">
            <a
              href={agenticUiLaunchUrl}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
            >
              <span>Get your API token in one click</span>
              <ArrowRightIcon className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
